import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronDown, ChevronUp, Calendar, Pencil, Trash2, Send, XCircle, MessageSquare, Link2, Clock } from 'lucide-react';
import { toast } from 'sonner';

function tomorrowISO() {
  return new Date(Date.now() + 86400000).toISOString().slice(0, 10);
}

export function MessageCard({ message, viewerMember, allMembers, onRespond, onClose, onSnooze, onDelete, onEdit, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const [showRespond, setShowRespond] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [respondTo, setRespondTo] = useState('');
  const [sending, setSending] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState(tomorrowISO());

  const fmtDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return d; }
  };

  const fmtDateTime = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return d; }
  };

  const handleSendResponse = async () => {
    if (!responseText.trim()) { toast.error('Please write a response'); return; }
    if (!respondTo) { toast.error('Please select who to send to'); return; }
    setSending(true);
    try {
      await onRespond(message.id, responseText.trim(), viewerMember.id, respondTo);
      setResponseText('');
      setRespondTo('');
      setShowRespond(false);
    } catch { /* handled in parent */ }
    finally { setSending(false); }
  };

  const otherMembers = allMembers.filter(m => m.id !== viewerMember.id);

  return (
    <div className="border rounded-lg mt-2 bg-white" data-testid={`board-message-${message.id}`}>
      <div className="px-3 py-2 flex items-start gap-2">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <p className={`text-[13px] text-gray-700 ${expanded ? '' : 'line-clamp-2'}`}>{message.content}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {message.due_date && (
              <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                <Calendar className="h-3 w-3" /> {fmtDate(message.due_date)}
              </span>
            )}
            {message.linked_entities?.length > 0 && (
              <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                <Link2 className="h-3 w-3" /> {message.linked_entities.length} linked
              </span>
            )}
            <span className="text-[10px] text-gray-300">{fmtDateTime(message.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button onClick={() => setExpanded(!expanded)} className="p-1 text-gray-400 hover:text-gray-600">
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          <button onClick={() => onEdit(message)} className="p-1 text-gray-400 hover:text-teal-600" title="Edit" data-testid={`edit-msg-${message.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => { if (window.confirm('Delete this message?')) onDelete(message.id); }} className="p-1 text-gray-400 hover:text-red-500" title="Delete" data-testid={`delete-msg-${message.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded: linked entities + response history */}
      {expanded && (
        <div className="px-3 pb-2 space-y-2 border-t">
          {message.linked_entities?.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-2">
              {message.linked_entities.map((e, i) => (
                <span key={i} className="text-[10px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">
                  <span className="capitalize">{e.type}</span>: {e.label}
                </span>
              ))}
            </div>
          )}
          {message.responses?.length > 0 && (
            <div className="space-y-1 pt-1">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Conversation</p>
              {message.responses.map((r, i) => (
                <div key={i} className="text-[12px] px-2 py-1.5 bg-gray-50 rounded border border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">{r.by_name} <span className="font-normal text-gray-400">to</span> {r.to_name}</span>
                    <span className="text-[10px] text-gray-400">{fmtDateTime(r.created_at)}</span>
                  </div>
                  <p className="text-gray-600 mt-0.5">{r.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action bar */}
      <div className="px-3 pb-2 flex flex-wrap gap-1.5 border-t pt-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[11px] gap-1"
          onClick={() => setShowRespond(!showRespond)}
          data-testid={`respond-btn-${message.id}`}
        >
          <MessageSquare className="h-3 w-3" /> Respond
        </Button>
        {onSnooze && (
          <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11px] gap-1 text-amber-700 border-amber-200 hover:bg-amber-50"
                data-testid={`snooze-btn-${message.id}`}
              >
                <Clock className="h-3 w-3" /> Snooze
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-3 space-y-2" data-testid={`snooze-popover-${message.id}`}>
              <p className="text-[11px] font-medium text-gray-500">Come back on…</p>
              <input
                type="date"
                value={snoozeDate}
                min={tomorrowISO()}
                onChange={e => setSnoozeDate(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid={`snooze-date-${message.id}`}
              />
              <div className="flex justify-end gap-1.5 pt-1">
                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setSnoozeOpen(false)}>Cancel</Button>
                <Button
                  size="sm"
                  className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700"
                  onClick={() => {
                    if (!snoozeDate) { toast.error('Pick a date first'); return; }
                    if (snoozeDate <= new Date().toISOString().slice(0, 10)) {
                      toast.error('Pick a future date');
                      return;
                    }
                    onSnooze(message.id, snoozeDate);
                    setSnoozeOpen(false);
                  }}
                  data-testid={`snooze-confirm-${message.id}`}
                >
                  Snooze
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[11px] gap-1 text-red-600 border-red-200 hover:bg-red-50"
          onClick={() => onClose(message.id, viewerMember.name)}
          data-testid={`close-btn-${message.id}`}
        >
          <XCircle className="h-3 w-3" /> Close
        </Button>
      </div>

      {/* Respond form */}
      {showRespond && (
        <div className="px-3 pb-3 space-y-2 border-t bg-gray-50/50">
          <div className="pt-2">
            <p className="text-[11px] font-medium text-gray-500 mb-1">Send to:</p>
            <div className="flex flex-wrap gap-1.5">
              {otherMembers.map(m => (
                <button
                  key={m.id}
                  onClick={() => setRespondTo(m.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                    respondTo === m.id ? 'bg-teal-50 border-teal-300 text-teal-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid={`respond-to-${m.id}`}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>
          <Textarea
            value={responseText}
            onChange={e => setResponseText(e.target.value)}
            placeholder="Write your response..."
            className="min-h-[50px] text-[12px] bg-white"
            data-testid={`response-text-${message.id}`}
          />
          <Button
            size="sm"
            className="h-7 text-[11px] bg-teal-600 hover:bg-teal-700 gap-1"
            onClick={handleSendResponse}
            disabled={sending}
            data-testid={`send-response-${message.id}`}
          >
            <Send className="h-3 w-3" /> {sending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      )}
    </div>
  );
}
